/**
 * Individual form types — match backend individual-forms.js schema exactly.
 * Date strings: MM/DD/YYYY.
 */

import type { USStateCode } from "./wizard.types";

export const PERSON_TYPE = {
  TAXPAYER: "TAXPAYER",
  SPOUSE: "SPOUSE",
  DEPENDENT: "DEPENDENT",
} as const;

export const RELATION_WITH_TAXPAYER = {
  SON: "SON",
  DAUGHTER: "DAUGHTER",
  FOSTER_CHILD: "FOSTER CHILD",
  GRANDCHILD: "GRANDCHILD",
  STEPCHILD: "STEPCHILD",
  GRANDPARENT: "GRANDPARENT",
  PARENT: "PARENT",
  BROTHER: "BROTHER",
  HALF_BROTHER: "HALF BROTHER",
  STEPBROTHER: "STEPBROTHER",
  SISTER: "SISTER",
  HALF_SISTER: "HALF SISTER",
  STEP_SISTER: "STEP SISTER",
  AUNT: "AUNT",
  UNCLE: "UNCLE",
  NEPHEW: "NEPHEW",
  NIECE: "NIECE",
  NONE: "NONE",
  OTHER: "OTHER",
} as const;

/** Filing status (Marital/Filing status dropdown: Single, MFJ, MFS, HOH, QW). */
export const INDIVIDUAL_FILING_STATUS = {
  SINGLE: "SINGLE",
  MARRIED_FILING_JOINTLY: "MARRIED_FILING_JOINTLY",
  MARRIED_FILING_SEPARATELY: "MARRIED_FILING_SEPARATELY",
  HEAD_OF_HOUSEHOLD: "HEAD_OF_HOUSEHOLD",
  QUALIFYING_WIDOW: "QUALIFYING_WIDOW",
} as const;

export const COVERAGE_SOURCE = {
  EMPLOYER: "EMPLOYER",
  SELF: "SELF",
  MARKETPLACE: "MARKETPLACE",
} as const;

export interface PersonalInfo {
  personType: "TAXPAYER" | "SPOUSE" | "DEPENDENT";
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dateOfBirth?: string;
  contact?: { emailId?: string; mobileNumber?: string };
  immigrationDetails?: { visaType?: string; firstDateOfEntry?: string };
  occupation?: string;
  maritalStatus?: string;
  ssn?: string;
  relationWithTaxPayer?: keyof typeof RELATION_WITH_TAXPAYER;
}

export interface AddressInformation {
  currentAddress?: string;
  city?: string;
  state?: USStateCode | string;
  zip?: string;
}

export interface ResidencyDetail {
  personType?: "TAXPAYER" | "SPOUSE";
  year?: number;
  state?: USStateCode | string;
  startDate?: string;
  endDate?: string;
}

export interface HealthCoverage {
  personType?: "TAXPAYER" | "SPOUSE" | "DEPENDENT";
  hasHealthCoverage?: boolean;
  coverageSource?: keyof typeof COVERAGE_SOURCE;
}

export interface EmployerInfo {
  employerName?: string;
  ein?: string;
  w2Income?: {
    wages?: number;
    federalTaxWithheld?: number;
    socialSecurityWages?: number;
    medicareWages?: number;
  };
  /** UI-only: state for W-2 (not in backend schema) */
  state?: string;
  /** UI-only: state tax withheld (not in backend schema) */
  stateTaxWithheld?: number;
}

export interface EmployerDetail {
  personType?: "TAXPAYER" | "SPOUSE";
  numberOfEmployers?: number;
  employerInfo?: EmployerInfo[];
}

/** Income details — backend fields: yes/no booleans plus optional amounts. */
export interface IncomeDetail {
  personType?: "TAXPAYER" | "SPOUSE";
  hasIncome?: boolean;
  w2Income?: number;
  /** Backend: didReceiveInterestOrDividend (1099-INT, 1099-DIV). */
  didReceiveInterestOrDividend?: boolean;
  interestIncome?: number;
  dividendIncome?: number;
  /** Backend: didReceiveIncomeFromSaleOfStocks (1099-B). */
  didReceiveIncomeFromSaleOfStocks?: boolean;
  /** Backend: didReceiveRefundFromStateLocalLastYear (1099-G). */
  didReceiveRefundFromStateLocalLastYear?: boolean;
  /** Backend: worksContractBasedIncome (1099-K, 1099-NEC). */
  worksContractBasedIncome?: boolean;
  /** Backend: anyOtherIncomeGambling (W-2G, bitcoin, etc.). */
  anyOtherIncomeGambling?: boolean;
  otherIncome?: { description?: string; amount?: number }[];
}

/** Deductions/expenses item — backend deductionsAndExpenses array element. */
export interface DeductionsAndExpensesDetail {
  personType?: "TAXPAYER" | "SPOUSE";
  homeMortgageInterestPaid?: boolean;
  contributionsToHSA?: boolean;
  contributionsToIRA?: boolean;
  studentLoanInterestPaid?: boolean;
  studentTuitionFeePaid?: boolean;
}

export interface Fbar {
  hasForeignBankAccount?: boolean;
  maximumAccountValue?: number;
  country?: string;
  /** UI/API: number of foreign accounts */
  numberOfAccounts?: number;
}

export interface Fatca {
  applicable?: boolean;
  assetValue?: number;
  country?: string;
}

export interface IndividualFormData {
  filingStatus?: keyof typeof INDIVIDUAL_FILING_STATUS;
  personalInformation?: PersonalInfo[];
  addressInformation?: AddressInformation;
  residencyDetails?: ResidencyDetail[];
  healthCoverageInformation?: HealthCoverage[];
  employerDetails?: EmployerDetail[];
  incomeDetails?: IncomeDetail[];
  /** Backend: deductions/expenses (Other Related Expenses). */
  deductionsAndExpenses?: DeductionsAndExpensesDetail[];
  fbar?: Fbar;
  fatca?: Fatca;
}

export const INDIVIDUAL_STEPS = [
  { id: "personal-info", title: "Personal Information" },
  { id: "residency", title: "Residency Details" },
  { id: "health", title: "Health Coverage" },
  { id: "employer", title: "Employer Details" },
  { id: "others-and-documents", title: "Others & Documents" },
] as const;

export type IndividualStepId = (typeof INDIVIDUAL_STEPS)[number]["id"];
